import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React from 'react';
import { StockLocation } from '../../../../types/stock_location';
import { TagRelation } from '../../../../types/tag/tagRelation';
import { translate } from '../../../../types/translation/Translator';
import { LocationMarkerIcon } from '../../../icons/LocationMarker/LocationMarkerIcon';
import { itemsContainText } from '../../../../util/string.util';
import { BaseFilter, BaseFilterProps } from '../common/BaseFilter';
import { FilterProps, FilterStorageKey } from '../common/filter.util';
import { TagFilter } from '../TagFilter';
import { StockLocationFilterInnerContent, StockLocationFilterDropdownContent } from './DropdownContent';
import { AddressFilter } from '../AddressFilter';
import { EntityType } from '../../../../types/comment';
import { Company } from '../../../../types/company';
import { CompanyPartnerFilter } from '../CompanyPartnerFilter/CompanyPartnerFilter';

export const StockLocationFilterMode = {
  all: 'all',
  source: 'source',
  destination: 'destination',
} as const;

export type StockLocationFilterMode = (typeof StockLocationFilterMode)[keyof typeof StockLocationFilterMode];

export class StockLocationFilter extends BaseFilter<StockLocation> {
  tagRelations: Map<string, TagRelation>;
  company: Company;
  mode: StockLocationFilterMode;

  constructor(
    tagRelations: Map<string, TagRelation>,
    company: Company,
    mode: StockLocationFilterMode = StockLocationFilterMode.all,
  ) {
    super(FilterStorageKey.STOCK_LOCATION);
    this.tagRelations = tagRelations;
    this.company = company;
    this.mode = mode;
  }

  filterOptions(): BaseFilter<any>[] {
    return [...(this.company.partner ? [new CompanyPartnerFilter()] : []), new TagFilter(EntityType.stockLocation)];
  }

  toLabel(): string {
    switch (this.mode) {
      case StockLocationFilterMode.source:
        return translate({ key: TranslationKey.sourceStockLocation });
      case StockLocationFilterMode.destination:
        return translate({ key: TranslationKey.destinationStockLocation });
      case StockLocationFilterMode.all:
        return translate({ key: TranslationKey.stockLocation });
    }
  }

  toCount(props: BaseFilterProps): number {
    switch (this.mode) {
      case StockLocationFilterMode.source:
        return props.filterProps.sourceStockLocation?.size || 0;
      case StockLocationFilterMode.destination:
        return props.filterProps.destinationStockLocation?.size || 0;
      case StockLocationFilterMode.all:
        return props.filterProps.stockLocation?.size || 0;
    }
  }

  toIcon(): JSX.Element {
    return <LocationMarkerIcon className={this.className} />;
  }

  toDropdownContent(props: BaseFilterProps): JSX.Element {
    return (
      <StockLocationFilterDropdownContent
        filter={this}
        filterProps={props.filterProps}
        setFilterProps={props.setFilterProps}
        mode={this.mode}
      />
    );
  }

  toInnerContent(props: BaseFilterProps): string | JSX.Element {
    return (
      <StockLocationFilterInnerContent
        filter={this}
        filterProps={props.filterProps}
        setFilterProps={props.setFilterProps}
        mode={this.mode}
      />
    );
  }

  search(item: StockLocation, query: string) {
    return itemsContainText(query, [item.name, item.identifier]) || new AddressFilter().search(query, item.address);
  }

  filteredItems(items: StockLocation[], query: string, filterProps: FilterProps) {
    return items.filter(item => {
      return [
        TagFilter.filterBy(filterProps, EntityType.stockLocation, this.tagRelations, item.id),
        StockLocationFilter.filter(filterProps, item),
        this.search(item, query),
      ].every(value => value === null || value);
    });
  }

  static filter(
    filterProps: FilterProps,
    item?: StockLocation,
    mode: StockLocationFilterMode = StockLocationFilterMode.all,
  ) {
    const itemId = item?.id || '';

    if (item && filterProps.company?.size && !filterProps.company.has(item.companyId || '')) {
      return false;
    }

    switch (mode) {
      case StockLocationFilterMode.source:
        if (filterProps.sourceStockLocation?.size && !filterProps.sourceStockLocation.has(itemId)) {
          return false;
        }
        break;
      case StockLocationFilterMode.destination:
        if (filterProps.destinationStockLocation?.size && !filterProps.destinationStockLocation.has(itemId)) {
          return false;
        }
        break;
      case StockLocationFilterMode.all:
        if (filterProps.stockLocation?.size && !filterProps.stockLocation.has(itemId)) {
          return false;
        }
        break;
    }

    return true;
  }
}
